import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  Home,
  Hammer,
  DollarSign,
  Shield,
  Zap
} from "lucide-react";

/**
 * DealForge Execution Network Map
 * 
 * Interactive SVG visualization showing DealForge at the center
 * with connections to all partner types in the ecosystem.
 */

// Partner node configurations
const PARTNER_NODES = [
  {
    id: "investor",
    label: "Investors",
    icon: TrendingUp,
    color: "#D4AF37",
    angle: 270, // Top
    link: "/investors",
    description: "Deal sourcing & execution"
  },
  {
    id: "realtor",
    label: "Realtors",
    icon: Home,
    color: "#D4AF37",
    angle: 342, // Top-right
    link: "/realtors",
    description: "Buyer matching & listings"
  },
  {
    id: "lender",
    label: "Lenders",
    icon: DollarSign,
    color: "#10B981",
    angle: 54, // Right
    link: "/lenders",
    description: "Capital deployment"
  },
  {
    id: "title",
    label: "Title",
    icon: Shield,
    color: "#10B981",
    angle: 126, // Bottom-right
    link: "/title",
    description: "Closing & Settlement"
  },
  {
    id: "contractor",
    label: "Contractors",
    icon: Hammer,
    color: "#3B82F6",
    angle: 198, // Bottom-left
    link: "/contractors",
    description: "Renovation & rehab"
  }
];

// Calculate position on ellipse
function getNodePosition(angle, radiusX, radiusY, centerX, centerY) {
  const rad = (angle * Math.PI) / 180;
  return {
    x: centerX + radiusX * Math.cos(rad),
    y: centerY + radiusY * Math.sin(rad)
  };
}

// Animated connection line
function ConnectionLine({ startX, startY, endX, endY, color, delay }) {
  return (
    <g>
      {/* Background line */}
      <line
        x1={startX}
        y1={startY}
        x2={endX}
        y2={endY}
        stroke={color}
        strokeWidth="2"
        strokeOpacity="0.2"
      />
      {/* Animated pulse line */}
      <line
        x1={startX}
        y1={startY}
        x2={endX}
        y2={endY}
        stroke={color}
        strokeWidth="2"
        strokeOpacity="0.6"
        strokeDasharray="8 8"
        style={{
          animation: `dash 2s linear infinite`,
          animationDelay: `${delay}s`
        }}
      />
      {/* Data flow particles */}
      <circle r="3" fill={color}>
        <animateMotion
          dur="3s"
          repeatCount="indefinite"
          begin={`${delay}s`}
          path={`M${startX},${startY} L${endX},${endY}`}
        />
        <animate
          attributeName="opacity"
          values="0;1;1;0"
          dur="3s"
          repeatCount="indefinite"
          begin={`${delay}s`}
        />
      </circle>
    </g>
  );
}

// Partner node component
function PartnerNode({ node, position, isHovered, onHover, onLeave, size = "normal" }) {
  const Icon = node.icon;
  const nodeRadius = size === "small" ? 32 : 44;
  const iconSize = size === "small" ? 16 : 22;
  const fontSize = size === "small" ? 10 : 12;
  
  return (
    <g
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={onLeave}
      style={{ cursor: "pointer" }}
    >
      <Link to={node.link}>
        {/* Outer glow */}
        <circle
          cx={position.x}
          cy={position.y}
          r={nodeRadius + 8}
          fill={node.color}
          opacity={isHovered ? 0.3 : 0.1}
          style={{ transition: "opacity 0.3s ease" }}
        />
        
        {/* Main circle */}
        <circle
          cx={position.x}
          cy={position.y}
          r={nodeRadius}
          fill="#0A0A0A"
          stroke={node.color}
          strokeWidth={isHovered ? 3 : 2}
          style={{ transition: "all 0.3s ease" }}
        />
        
        {/* Icon background */}
        <circle
          cx={position.x}
          cy={position.y - (size === "small" ? 4 : 6)}
          r={iconSize + 4}
          fill={`${node.color}20`}
        />
        
        {/* Icon - using foreignObject for Lucide icons */}
        <foreignObject
          x={position.x - iconSize}
          y={position.y - iconSize - (size === "small" ? 4 : 6)}
          width={iconSize * 2}
          height={iconSize * 2}
        >
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            width: "100%",
            height: "100%"
          }}>
            <Icon 
              style={{ 
                width: iconSize, 
                height: iconSize, 
                color: node.color 
              }} 
            />
          </div>
        </foreignObject>
        
        {/* Label */}
        <text
          x={position.x}
          y={position.y + (size === "small" ? 16 : 22)}
          textAnchor="middle"
          fill="white"
          fontSize={fontSize}
          fontWeight="600"
          fontFamily="system-ui, sans-serif"
        >
          {node.label}
        </text>
        
        {/* Description (on hover) */}
        {isHovered && size !== "small" && (
          <text
            x={position.x}
            y={position.y + 36}
            textAnchor="middle"
            fill="rgba(255,255,255,0.6)"
            fontSize="10"
            fontFamily="system-ui, sans-serif"
          >
            {node.description}
          </text>
        )}
      </Link>
    </g>
  );
}

// Main Network Map component
export default function NetworkMap({ variant = "full", className = "" }) {
  const [hoveredNode, setHoveredNode] = useState(null);
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Dimensions based on variant
  const config = {
    full: { width: 700, height: 500, radiusX: 260, radiusY: 180, centerY: 250 },
    compact: { width: 500, height: 350, radiusX: 180, radiusY: 130, centerY: 175 },
    mini: { width: 350, height: 280, radiusX: 130, radiusY: 100, centerY: 150 }
  };
  
  const { width, height, radiusX, radiusY, centerY } = config[variant] || config.full;
  const centerX = width / 2;
  const centerRadius = variant === "mini" ? 40 : variant === "compact" ? 50 : 60;
  
  // Calculate node positions
  const nodePositions = PARTNER_NODES.map(node => ({
    ...node,
    position: getNodePosition(node.angle, radiusX, radiusY, centerX, centerY)
  }));
  
  return (
    <div className={`relative ${className}`}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        style={{ 
          maxWidth: width,
          opacity: mounted ? 1 : 0,
          transition: "opacity 0.5s ease"
        }}
      >
        <defs>
          {/* Gradient for center node */}
          <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
          
          {/* Animation keyframes */}
          <style>
            {`
              @keyframes dash {
                to {
                  stroke-dashoffset: -16;
                }
              }
              @keyframes pulse {
                0%, 100% { r: ${centerRadius}; opacity: 1; }
                50% { r: ${centerRadius + 5}; opacity: 0.8; }
              }
              @keyframes rotate {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </defs>
        
        {/* Background grid pattern */}
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path 
            d="M 40 0 L 0 0 0 40" 
            fill="none" 
            stroke="rgba(255,255,255,0.03)" 
            strokeWidth="1"
          />
        </pattern>
        <rect width="100%" height="100%" fill="url(#grid)" />
        
        {/* Connection lines */}
        {nodePositions.map((node, idx) => (
          <ConnectionLine
            key={node.id}
            startX={centerX}
            startY={centerY}
            endX={node.position.x}
            endY={node.position.y}
            color={node.color}
            delay={idx * 0.4}
          />
        ))}
        
        {/* Center glow effect */}
        <circle
          cx={centerX}
          cy={centerY}
          r={centerRadius + 30}
          fill="url(#centerGlow)"
        />
        
        {/* Center node - DealForge */}
        <g>
          {/* Rotating outer ring */}
          <circle
            cx={centerX}
            cy={centerY}
            r={centerRadius + 12}
            fill="none"
            stroke="#D4AF37"
            strokeWidth="1"
            strokeDasharray="4 8"
            opacity="0.4"
            style={{
              transformOrigin: `${centerX}px ${centerY}px`,
              animation: "rotate 20s linear infinite"
            }}
          />
          
          {/* Main center circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r={centerRadius}
            fill="#0A0A0A"
            stroke="#D4AF37"
            strokeWidth="3"
            style={{ animation: "pulse 3s ease-in-out infinite" }}
          />
          
          {/* Inner accent */}
          <circle
            cx={centerX}
            cy={centerY}
            r={centerRadius - 8}
            fill="none"
            stroke="#D4AF37"
            strokeWidth="1"
            opacity="0.3"
          />
          
          {/* Zap icon */}
          <foreignObject
            x={centerX - 16}
            y={centerY - 24}
            width={32}
            height={32}
          >
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              width: "100%",
              height: "100%"
            }}>
              <Zap 
                style={{ 
                  width: variant === "mini" ? 20 : 24, 
                  height: variant === "mini" ? 20 : 24, 
                  color: "#D4AF37",
                  filter: "drop-shadow(0 0 8px rgba(212, 175, 55, 0.6))"
                }} 
              />
            </div>
          </foreignObject>
          
          {/* DealForge text */}
          <text
            x={centerX}
            y={centerY + 16}
            textAnchor="middle"
            fill="#D4AF37"
            fontSize={variant === "mini" ? 10 : 12}
            fontWeight="700"
            fontFamily="system-ui, sans-serif"
            letterSpacing="0.5"
          >
            DEALFORGE
          </text>
        </g>
        
        {/* Partner nodes */}
        {nodePositions.map(node => (
          <PartnerNode
            key={node.id}
            node={node}
            position={node.position}
            isHovered={hoveredNode === node.id}
            onHover={setHoveredNode}
            onLeave={() => setHoveredNode(null)}
            size={variant === "mini" ? "small" : "normal"}
          />
        ))}
        
        {/* "Live Network" indicator */}
        {variant !== "mini" && (
          <g>
            <circle cx={width - 70} cy={30} r="4" fill="#00FF88">
              <animate
                attributeName="opacity"
                values="1;0.4;1"
                dur="2s"
                repeatCount="indefinite"
              />
            </circle>
            <text
              x={width - 58}
              y={34}
              fill="#00FF88"
              fontSize="11"
              fontWeight="600"
              fontFamily="system-ui, sans-serif"
            >
              LIVE NETWORK
            </text>
          </g>
        )}
      </svg>
      
      {/* Caption */}
      {variant === "full" && (
        <p className="text-center text-white/50 text-sm mt-4">
          Click any node to explore that portal
        </p>
      )}
    </div>
  );
}

// Compact version for cards/sidebars
export function NetworkMapMini({ className = "" }) {
  return <NetworkMap variant="mini" className={className} />;
}

// Medium version for sections
export function NetworkMapCompact({ className = "" }) {
  return <NetworkMap variant="compact" className={className} />;
}
